import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { fmtUGX } from "@/lib/format";
import {
  Smartphone, Hash, CheckCircle2, Loader2, Sparkles, Plus, Minus, Wallet,
  ArrowRight, Receipt, Zap, Shield, Copy, XCircle, CreditCard, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { lookupAccount, normaliseMsisdn, initiateMomoTopup, pollKacyberPaymentStatus, simulatePayment } from "@/lib/momo.functions";
import { useEffectiveBrand } from "@/lib/tenant-branding";

/** Per-tenant product name for the consolidated top-up rail. */
function payProductName(slug: string | null, fallback: string): string {
  if (slug === "fundred") return "FundredPay";
  if (slug === "tricampus") return "TriSphere Pay";
  if (slug === "st-marys") return "St Mary's Pay";
  return `${fallback} Pay`;
}

type Rail = "momo" | "card" | "prn";

/* ============================================================
   TriSphere Pay — Smart Consolidated Top-up
   One MoMo push, one PRN, fan-out to every child + every service.
============================================================ */

type Bucket = "fees" | "transport" | "canteen" | "uniform" | "treasury";
const BUCKETS: { k: Bucket; label: string; emoji: string; tone: string }[] = [
  { k: "fees",      label: "School fees",   emoji: "🎓", tone: "#7B1416" },
  { k: "transport", label: "Transport",     emoji: "🚌", tone: "#8B6F47" },
  { k: "canteen",   label: "Canteen",       emoji: "🍱", tone: "#A42022" },
  { k: "uniform",   label: "Uniform",       emoji: "👕", tone: "#C4A882" },
  { k: "treasury",  label: "Family wallet", emoji: "💰", tone: "#4A4036" },
];

const TARIFF: { hi: number; v: number }[] = [
  { hi: 2500, v: 110 }, { hi: 5000, v: 220 }, { hi: 15000, v: 330 },
  { hi: 30000, v: 440 }, { hi: 45000, v: 550 }, { hi: 60000, v: 880 },
  { hi: 125000, v: 1500 }, { hi: 250000, v: 2800 }, { hi: 500000, v: 4000 },
  { hi: 1000000, v: 5500 }, { hi: 2000000, v: 9500 }, { hi: 4000000, v: 15000 },
  { hi: 7000000, v: 18500 },
];
const tariff = (amt: number) => (TARIFF.find(b => amt <= b.hi) ?? TARIFF[TARIFF.length - 1]).v;

function genPRN() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = ""; for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return "SCH-" + s;
}
function genWTX(seed: string) {
  return `WTX-${seed}-${Math.floor(100000 + Math.random() * 899999)}`;
}

type Student = { id: string; full_name: string; initials: string; color_hex: string };
type Fee = { student_id: string; total_ugx: number; paid_ugx: number };

type Allocation = { sid: string | null; name: string; tone: string; initials: string; bucket: Bucket; amount: number; wtx?: string };
type Stage = "build" | "review" | "push" | "confirm" | "fanout" | "done" | "failed";

export function TriSpherePayTopUp({
  walletId, students, fees, trigger, defaultPhone,
  presetStudentId, presetAmount, presetBucket, presetLabel,
}: {
  walletId?: string;
  students: Student[];
  fees: Fee[];
  trigger: React.ReactNode;
  defaultPhone?: string;
  /** When set, the modal opens with a single allocation for this student only */
  presetStudentId?: string;
  /** When set with presetStudentId, the allocation amount is fixed to this value */
  presetAmount?: number;
  /** When set, the allocation bucket is fixed (e.g. "fees") */
  presetBucket?: Bucket;
  /** Optional label override for the preset allocation */
  presetLabel?: string;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { brand } = useEffectiveBrand();
  const productName = payProductName(brand.tenantSlug, brand.name);
  const brandPrimary = brand.primaryColor || "#7B1416";
  const brandAccent = brand.accentColor || "#C4A882";
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("build");
  const [rail, setRail] = useState<Rail>("momo");
  const [operator, setOperator] = useState<"mtn" | "airtel">("mtn");
  const [phone, setPhone] = useState(defaultPhone ?? "");
  const [prn, setPrn] = useState("— — — —");
  const [opTxn, setOpTxn] = useState("");
  const [allocs, setAllocs] = useState<Allocation[]>([]);
  const [fanCursor, setFanCursor] = useState(0);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [kacyberRef, setKacyberRef] = useState("");
  const [simulating, setSimulating] = useState(false);
  const timers = useRef<number[]>([]);

  // Seed allocations: if presetStudentId is set, show only that child with the exact amount.
  // Otherwise, seed sensible defaults from outstanding fees + a treasury top-up.
  useEffect(() => {
    if (!open) return;
    if (allocs.length > 0) return;
    const seeded: Allocation[] = [];
    if (presetStudentId) {
      const s = students.find(x => x.id === presetStudentId);
      if (s) {
        seeded.push({
          sid: s.id, name: s.full_name, tone: s.color_hex, initials: s.initials,
          bucket: presetBucket ?? "fees",
          amount: presetAmount ?? 0,
        });
      }
    } else {
      students.forEach(s => {
        const f = fees.find(x => x.student_id === s.id);
        const outstanding = f ? Math.max(0, f.total_ugx - f.paid_ugx) : 0;
        seeded.push({ sid: s.id, name: s.full_name, tone: s.color_hex, initials: s.initials,
          bucket: "fees", amount: outstanding > 0 ? Math.min(outstanding, Math.round(outstanding / 4 / 1000) * 1000) : 100000 });
        seeded.push({ sid: s.id, name: s.full_name, tone: s.color_hex, initials: s.initials, bucket: "canteen",   amount: 20000 });
        seeded.push({ sid: s.id, name: s.full_name, tone: s.color_hex, initials: s.initials, bucket: "transport", amount: 15000 });
      });
      if (students.length === 0) {
        seeded.push({ sid: null, name: "Family wallet", tone: "#7B1416", initials: "★", bucket: "treasury", amount: 150000 });
      }
    }
    setAllocs(seeded);
  }, [open, students, fees, allocs.length, presetStudentId, presetAmount, presetBucket]);

  const total = useMemo(() => allocs.reduce((s, a) => s + (a.amount || 0), 0), [allocs]);
  const oldCharge = useMemo(() => allocs.reduce((s, a) => s + (a.amount > 0 ? tariff(a.amount) : 0), 0), [allocs]);
  const newCharge = tariff(total);
  const saved = Math.max(0, oldCharge - newCharge);

  function reset() {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
    setStage("build"); setPrn("— — — —"); setOpTxn(""); setFanCursor(0);
    setAccountName(null); setLookupError(null);
    setKacyberRef("");
    setAllocs(a => a.map(x => ({ ...x, wtx: undefined })));
  }

  async function verifyAccount() {
    const cleaned = phone.replace(/\s/g, "").replace(/^\+/, "");
    if (!/^(256|0)?7\d{8}$/.test(cleaned)) {
      setLookupError("Enter a valid Ugandan number (e.g. 0772XXXXXX or +256772XXXXXX)");
      return;
    }
    setLookupLoading(true); setLookupError(null); setAccountName(null);
    const res = await lookupAccount({
      msisdn: normaliseMsisdn(phone),
      network: operator === "mtn" ? "MTN" : "AIRTEL",
    });
    setLookupLoading(false);
    if (res.success && res.accountName) {
      setAccountName(res.accountName);
      toast.success(`Verified: ${res.accountName}`);
    } else {
      setLookupError(res.message ?? "Could not verify account");
    }
  }

  function edit(idx: number, delta: number) {
    setAllocs(prev => prev.map((a, i) => i === idx ? { ...a, amount: Math.max(0, a.amount + delta) } : a));
  }
  function setAmt(idx: number, v: number) {
    setAllocs(prev => prev.map((a, i) => i === idx ? { ...a, amount: Math.max(0, v) } : a));
  }
  function addLine(sid: string | null, name: string, tone: string, initials: string, bucket: Bucket) {
    setAllocs(prev => [...prev, { sid, name, tone, initials, bucket, amount: 10000 }]);
  }
  function removeLine(idx: number) { setAllocs(prev => prev.filter((_, i) => i !== idx)); }

  async function getPaymentWalletId() {
    if (walletId) return walletId;
    if (!user?.id) throw new Error("Sign in again to continue");

    const { data: existing, error: lookupError } = await supabase
      .from("wallets")
      .select("id")
      .eq("owner_id", user.id)
      .eq("owner_type", "parent")
      .maybeSingle();
    if (lookupError) throw lookupError;
    if (existing?.id) return existing.id;

    const { data: created, error: createError } = await supabase
      .from("wallets")
      .insert({ owner_id: user.id, owner_type: "parent", balance_ugx: 0 })
      .select("id")
      .single();
    if (createError) throw createError;
    return created.id;
  }

  const submit = useMutation({
    mutationFn: async () => {
      const paymentWalletId = await getPaymentWalletId();
      if (total < 500) throw new Error("Minimum top-up is UGX 500");
      const cleanedPhone = phone.replace(/\s/g, "").replace(/^\+/, "");
      if (rail === "momo" && !/^(256|0)?7\d{8}$/.test(cleanedPhone)) {
        throw new Error("Enter a valid Ugandan MoMo number (e.g. 0772XXXXXX or +256772XXXXXX)");
      }

      const channel: "mtn_momo" | "airtel_money" | "card" | "bank" = rail === "momo"
        ? (operator === "mtn" ? "mtn_momo" : "airtel_money")
        : rail === "card" ? "card" : "bank";

      setStage("push");

      let reference: string = genPRN();
      let topupId: string = "";

      // Create the topup record
      setPrn(reference);
      const { data: topup, error: e1 } = await supabase.from("topups").insert({
        wallet_id: paymentWalletId, initiated_by: user!.id,
        amount_ugx: total, fee_ugx: newCharge, channel, phone: phone || "n/a",
        reference, status: "pending",
      }).select("id").single();
      if (e1) throw e1;
      topupId = topup!.id;

      if (rail === "momo") {
        // Push flow: KaCyber sends a prompt to the customer's phone
        let kcRef = "";
        try {
          const res = await initiateMomoTopup({
            wallet_id: paymentWalletId, topup_id: topupId,
            amount_ugx: total, fee_ugx: newCharge,
            channel: channel as "mtn_momo" | "airtel_money",
            phone, customer_name: accountName ?? undefined,
            narration: `${productName} top-up`, external_reference: reference,
          });
          reference = res.reference ?? reference;
          kcRef = res.kacyber_reference ?? "";
          setKacyberRef(kcRef);
          if (res.prn) setPrn(res.prn);
          else setPrn(reference);
          // SMS failure is non-critical — the MoMo push prompt is the primary channel.
        } catch (err: any) {
          await supabase.from("topups").update({ status: "failed" }).eq("id", topupId);
          setStage("failed");
          throw new Error(err?.message ?? "KaCyber rejected the push");
        }

        // Poll KaCyber status — no timeout error, keep waiting indefinitely.
        // The callback webhook or poll-status will credit the wallet.
        setStage("confirm");
        let confirmed = false;
        while (true) {
          await new Promise(r => setTimeout(r, 4000));
          const poll = await pollKacyberPaymentStatus(reference, kcRef);
          if (poll.payment_status === "completed") { confirmed = true; break; }
          if (poll.payment_status === "failed") {
            setStage("failed");
            throw new Error(poll.kc_error ?? "Payment was declined");
          }
          if (poll.payment_status === "expired") {
            setStage("failed");
            throw new Error("The payment prompt expired — you didn't approve it in time. Please try again.");
          }
          // Keep waiting — no timeout error
        }

        if (!confirmed) { setStage("failed"); throw new Error("Payment was not confirmed"); }
      } else {
        // Card / PRN flow: poll briefly, then sim-credit
        const started = Date.now();
        const maxWait = 3500;
        setStage("confirm");
        let confirmed = false;
        while (Date.now() - started < maxWait) {
          await new Promise(r => setTimeout(r, 3000));
          const { data } = await supabase.from("topups").select("status").eq("id", topupId).maybeSingle();
          if (data?.status === "posted") { confirmed = true; break; }
          if (data?.status === "failed") { setStage("failed"); throw new Error("Payment declined or timed out"); }
        }

        if (!confirmed) {
          const { data: st } = await supabase.from("topups").select("status").eq("id", topupId).maybeSingle();
          if (st?.status !== "posted") {
            const { data: w } = await supabase.from("wallets").select("balance_ugx").eq("id", paymentWalletId).single();
            const credited = total - newCharge;
            await supabase.from("wallets").update({ balance_ugx: (w?.balance_ugx ?? 0) + credited }).eq("id", paymentWalletId);
            const { data: tx } = await supabase.from("transactions").insert({
              wallet_id: paymentWalletId, type: "topup", status: "posted",
              amount_ugx: total, fee_ugx: newCharge,
              description: `TriSphere Pay · ${rail.toUpperCase()} · ${allocs.length} allocations`,
              reference,
            }).select("id").single();
            await supabase.from("topups").update({ status: "posted", transaction_id: tx?.id }).eq("id", topupId);
          }
        }
      }

      if (!opTxn) setOpTxn(rail === "momo" ? `${operator.toUpperCase()}-${reference.slice(-9)}` : reference);
      setStage("fanout");
      const p = reference;

      // Fan out each allocation: parent_ledger credit + student wallet credit + wallet_allocations record
      const fanned: Allocation[] = [];
      for (let i = 0; i < allocs.length; i++) {
        const a = allocs[i];
        const wtx = genWTX((a.sid ?? "FAM").slice(0, 4).toUpperCase());

        // 1. Parent ledger entry
        await supabase.from("parent_ledger").insert({
          guardian_id: user!.id, student_id: a.sid,
          kind: a.bucket === "treasury" ? "topup" : a.bucket,
          direction: "credit",
          title: `${BUCKETS.find(b => b.k === a.bucket)?.emoji ?? "•"} ${a.name} · ${BUCKETS.find(b => b.k === a.bucket)?.label ?? a.bucket}`,
          subtitle: `PRN ${p} · ${wtx}`,
          amount_ugx: a.amount, ref: wtx,
        });

        // 2. Credit the student's wallet if this allocation is for a specific student
        if (a.sid) {
          const { data: studentWallet } = await supabase
            .from("wallets")
            .select("id, balance_ugx")
            .eq("owner_id", a.sid)
            .eq("owner_type", "student")
            .maybeSingle();

          if (studentWallet) {
            await supabase.from("wallets").update({
              balance_ugx: (studentWallet.balance_ugx ?? 0) + a.amount,
            }).eq("id", studentWallet.id);

            // 3. Create wallet_allocations record
            await supabase.from("wallet_allocations").insert({
              topup_id: topupId,
              source_wallet_id: paymentWalletId,
              destination_wallet_id: studentWallet.id,
              student_id: a.sid,
              bucket: a.bucket,
              amount_ugx: a.amount,
              wtx_reference: wtx,
              status: "posted",
            });

            // 4. Transaction record for the student wallet
            await supabase.from("transactions").insert({
              wallet_id: studentWallet.id,
              counterparty_wallet_id: paymentWalletId,
              type: "topup",
              status: "posted",
              amount_ugx: a.amount,
              fee_ugx: 0,
              description: `${productName} · ${BUCKETS.find(b => b.k === a.bucket)?.label ?? a.bucket} · ${wtx}`,
              reference: wtx,
              metadata: { prn: p, bucket: a.bucket, student_name: a.name },
            });
          }
        }

        fanned.push({ ...a, wtx });
        setAllocs(prev => prev.map((x, idx) => idx === i ? { ...x, wtx } : x));
        setFanCursor(i + 1);
        await new Promise(r => setTimeout(r, 180));
      }

      setStage("done");
      return { prn: p, fanned };
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`${fmtUGX(total)} settled · ${allocs.length} wallets updated`);
    },
    onError: (e: Error) => { toast.error(e.message); },
  });

  function close() {
    setOpen(false);
    setTimeout(() => { reset(); setAllocs([]); }, 300);
  }

  const stageIndex = ["build","review","push","confirm","fanout","done"].indexOf(stage);
  const stagePct = stage === "done" ? 100 : Math.max(5, (stageIndex / 5) * 100);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); setAllocs([]); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-4xl p-0 border-0 overflow-y-auto max-h-[92vh] sm:rounded-2xl rounded-t-2xl">
        <DialogHeader
          className="relative overflow-hidden border-b p-6 text-white"
          style={{ background: `linear-gradient(135deg, ${brandPrimary} 0%, color-mix(in oklab, ${brandPrimary} 55%, black) 100%)` }}
        >
          {/* atmospheric orbs */}
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-40" style={{ background: `radial-gradient(circle, ${brandAccent}, transparent 65%)` }} />
          <div className="pointer-events-none absolute -left-24 -bottom-24 h-64 w-64 rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${brandAccent}, transparent 70%)` }} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.24em] backdrop-blur">
                <Sparkles className="h-3 w-3" style={{ color: brandAccent }} />
                {productName} · consolidated top-up
              </div>
              <DialogTitle className="mt-3 font-extrabold text-2xl leading-tight text-white sm:text-3xl" style={{ letterSpacing: '-0.02em' }}>
                One push. Every child. Every wallet.
              </DialogTitle>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/70">
                <span>{allocs.length} allocations</span>
                <span>·</span>
                <span>PRN <span className="font-mono text-white/90">{prn}</span></span>
              </div>
            </div>
            <div className="shrink-0 text-left sm:text-right">
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/60">Total push</div>
              <div className="font-extrabold text-3xl tabular-nums leading-none sm:text-4xl" style={{ letterSpacing: '-0.02em' }}>{fmtUGX(total)}</div>
              {saved > 0 && stage === "build" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `color-mix(in oklab, ${brandAccent} 22%, transparent)`, color: brandAccent }}>
                  <Zap className="h-3 w-3" />Saves {fmtUGX(saved)}
                </div>
              )}
            </div>
          </div>

          {/* Segmented stepper */}
          <div className="relative mt-5 flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: "none" }}>
            {["Build","Review","Push","Confirm","Fanout","Done"].map((label, i) => {
              const done = stageIndex > i || stage === "done";
              const active = stageIndex === i;
              return (
                <div key={label} className="flex-1">
                  <div className="h-1 overflow-hidden rounded-full bg-white/15">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: done ? "100%" : active ? "60%" : "0%" }}
                      transition={{ duration: 0.45 }}
                      className="h-full rounded-full"
                      style={{ background: brandAccent }}
                    />
                  </div>
                  <div className={`mt-1.5 text-[9px] font-semibold uppercase tracking-wider ${active || done ? "text-white/90" : "text-white/40"}`}>{label}</div>
                </div>
              );
            })}
          </div>
        </DialogHeader>


        {(stage === "build" || stage === "review") && (
          <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-[1.4fr_1fr]">
            {/* LEFT: allocations */}
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Allocations</div>
                <div className="flex flex-wrap gap-1">
                  {students.map(s => (
                    <Button key={s.id} size="sm" variant="outline" className="h-7 text-[11px]"
                      onClick={() => addLine(s.id, s.full_name, s.color_hex, s.initials, "canteen")}>
                      <Plus className="mr-1 h-3 w-3" />{s.full_name.split(" ")[0]}
                    </Button>
                  ))}
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => addLine(null, "Family wallet", "#7B1416", "★", "treasury")}>
                    <Plus className="mr-1 h-3 w-3" />Family
                  </Button>
                </div>
              </div>

              <div className="max-h-[340px] space-y-2 overflow-auto pr-1">
                {allocs.map((a, i) => {
                  const b = BUCKETS.find(x => x.k === a.bucket)!;
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-2 sm:flex-nowrap">
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-semibold text-white"
                        style={{ background: a.tone }}>{a.initials}</div>
                      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                        <div className="truncate text-xs font-medium">{a.name}</div>
                        <select
                          value={a.bucket}
                          onChange={(e) => setAllocs(prev => prev.map((x, idx) => idx === i ? { ...x, bucket: e.target.value as Bucket } : x))}
                          className="mt-0.5 w-full rounded border bg-background px-1 py-0.5 text-[10px]"
                        >
                          {BUCKETS.map(opt => <option key={opt.k} value={opt.k}>{opt.emoji} {opt.label}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => edit(i, -5000)}><Minus className="h-3 w-3" /></Button>
                        <Input
                          value={a.amount}
                          onChange={(e) => setAmt(i, parseInt(e.target.value.replace(/\D/g, "") || "0", 10))}
                          className="h-7 w-20 text-right font-mono text-xs"
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => edit(i, +5000)}><Plus className="h-3 w-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => removeLine(i)}><XCircle className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  );
                })}
                {allocs.length === 0 && (
                  <div className="rounded-lg border border-dashed p-6 text-center text-xs text-muted-foreground">
                    Add an allocation above to start your consolidated push.
                  </div>
                )}
              </div>

              {/* Quick-fill */}
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Quick fill ·</span>
                {[50000, 100000, 200000, 500000].map(v => (
                  <Badge key={v} variant="outline" className="cursor-pointer text-[10px]"
                    onClick={() => allocs[0] && setAmt(0, v)}>
                    Line 1 → {fmtUGX(v)}
                  </Badge>
                ))}
              </div>
            </div>

            {/* RIGHT: channel + summary */}
            <div className="space-y-3">
              <Card className="p-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Payment method</Label>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                  {([
                    { k: "momo", label: "MoMo Push", icon: Smartphone },
                    { k: "card", label: "Card", icon: CreditCard },
                    { k: "prn",  label: "Bank / PRN", icon: Landmark },
                  ] as { k: Rail; label: string; icon: any }[]).map(r => {
                    const active = rail === r.k;
                    return (
                      <button key={r.k} type="button" onClick={() => setRail(r.k)}
                        style={active ? { borderColor: brandPrimary, background: `color-mix(in oklab, ${brandPrimary} 10%, transparent)`, color: brandPrimary } : undefined}
                        className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-medium transition ${active ? "" : "border-border bg-background text-muted-foreground hover:bg-muted/60"}`}>
                        <r.icon className="h-4 w-4" />
                        {r.label}
                      </button>
                    );
                  })}
                </div>

                {rail === "momo" && (
                  <>
                    <Tabs value={operator} onValueChange={(v) => { setOperator(v as any); setAccountName(null); setLookupError(null); }} className="mt-3">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="mtn">🟡 MTN MoMo</TabsTrigger>
                        <TabsTrigger value="airtel">🔴 Airtel Money</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <div className="mt-2 flex gap-2">
                      <Input
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); setAccountName(null); setLookupError(null); }}
                        placeholder="07XX XXX XXX"
                        className="flex-1 font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={lookupLoading || !phone}
                        onClick={verifyAccount}
                      >
                        {lookupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hash className="h-3.5 w-3.5" />}
                        Verify
                      </Button>
                    </div>
                    {accountName && (
                      <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span className="font-medium">{accountName}</span>
                      </div>
                    )}
                    {lookupError && (
                      <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700">
                        <XCircle className="h-3.5 w-3.5" />
                        <span>{lookupError}</span>
                      </div>
                    )}
                    <div className="mt-2 rounded-md border bg-primary/5 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                      A payment prompt will be sent to your phone. Open it and enter your MoMo PIN to approve.
                    </div>
                  </>
                )}
                {rail === "card" && (
                  <div className="mt-3 rounded-md border bg-muted/30 p-3 text-[11px] text-muted-foreground">
                    <CreditCard className="mb-1 inline h-3.5 w-3.5 text-[var(--forest)]" />{" "}
                    A secure card checkout window will open after you tap the button. Visa, Mastercard and school-issued cards are accepted. You'll be returned here once payment is confirmed.
                  </div>
                )}
                {rail === "prn" && (
                  <div className="mt-3 space-y-2 rounded-md border bg-muted/30 p-3 text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-2 text-foreground">
                      <Landmark className="h-4 w-4 text-[var(--forest)]" />
                      <span className="font-semibold">Pay by bank transfer using a PRN</span>
                    </div>
                    <p>Tap the button and we'll generate a Payment Reference Number. Use it as the narration in your mobile / internet banking app. Funds auto-reconcile once received.</p>
                  </div>
                )}
              </Card>

              <Card className="p-3 text-xs">
                <div className="font-bold text-sm" style={{ letterSpacing: '-0.01em' }}>Tariff comparison</div>
                <div className="mt-2 space-y-1">
                  <Row k={`${allocs.length} separate sends`} v={fmtUGX(oldCharge)} tone="rose" />
                  <Row k={`1 ${productName} push`} v={fmtUGX(newCharge)} tone="forest" />
                  <Row k="Saved this push" v={fmtUGX(saved)} tone="mustard" bold />
                </div>
                <div className="mt-3 rounded-md border bg-muted/40 p-2 text-[10px] text-muted-foreground">
                  <Shield className="mr-1 inline h-3 w-3" />
                  Net credited to wallet: <b className="text-foreground">{fmtUGX(Math.max(0, total - newCharge))}</b>.
                  Each allocation gets a unique WTX for reconciliation.
                </div>
              </Card>

              <Button
                disabled={total < 500 || submit.isPending}
                onClick={() => submit.mutate()}
                style={{ background: `linear-gradient(135deg, ${brandPrimary}, color-mix(in oklab, ${brandPrimary} 70%, black))` }}
                className="w-full text-white shadow-lg transition hover:opacity-95 hover:shadow-xl"
              >
                {rail === "momo" && <><Smartphone className="mr-2 h-4 w-4" />Send MoMo push · {fmtUGX(total)}</>}
                {rail === "card" && <><CreditCard className="mr-2 h-4 w-4" />Pay by card · {fmtUGX(total)}</>}
                {rail === "prn"  && <><Landmark className="mr-2 h-4 w-4" />Generate PRN · {fmtUGX(total)}</>}
              </Button>
            </div>

          </div>
        )}

        {(stage === "push" || stage === "confirm" || stage === "fanout") && (
          <div className="grid gap-4 p-4 sm:p-6 md:grid-cols-[1fr_1.2fr]">
            {/* Phone push / USSD instructions */}
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border bg-muted/30 p-6">
              {rail === "momo" && stage !== "fanout" ? (
                <>
                  <div className="relative">
                    <Smartphone className="h-14 w-14" style={{ color: brandPrimary }} />
                    {stage === "push" && (
                      <motion.span
                        animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
                        className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-amber-400 text-[10px] font-bold text-foreground"
                      >!</motion.span>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-base" style={{ letterSpacing: '-0.01em' }}>
                      {stage === "push" && `Check your ${operator === "mtn" ? "MTN" : "Airtel"} phone`}
                      {stage === "confirm" && "Waiting for your approval…"}
                    </div>
                    <div className="mt-1 font-mono text-xs text-muted-foreground">{phone}</div>
                  </div>
                  <Card className="w-full bg-card p-3 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">PRN</span><span className="font-mono">{prn}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono">{fmtUGX(total)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Op TXN</span><span className="font-mono">{opTxn || "—"}</span></div>
                  </Card>
                  {stage === "confirm" && (
                    <div className="text-center text-xs text-muted-foreground">
                      A payment prompt has been sent to your phone.<br />Open it and enter your MoMo PIN to approve {fmtUGX(total)}.
                    </div>
                  )}
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  {stage === "confirm" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-amber-400/50 text-amber-700 hover:bg-amber-50"
                      disabled={simulating}
                      onClick={async () => {
                        setSimulating(true);
                        try {
                          const result = await simulatePayment(prn);
                          if (result.payment_status === "completed") {
                            // The edge function credited the wallet + topup.
                            // Proceed to fan-out.
                            if (!opTxn) setOpTxn(`SIM-${prn.slice(-9)}`);
                            setStage("fanout");
                            const p = prn;
                            for (let i = 0; i < allocs.length; i++) {
                              const a = allocs[i];
                              const wtx = genWTX((a.sid ?? "FAM").slice(0, 4).toUpperCase());
                              await supabase.from("parent_ledger").insert({
                                guardian_id: user!.id, student_id: a.sid,
                                kind: a.bucket === "treasury" ? "topup" : a.bucket,
                                direction: "credit",
                                title: `${BUCKETS.find(b => b.k === a.bucket)?.emoji ?? "•"} ${a.name} · ${BUCKETS.find(b => b.k === a.bucket)?.label ?? a.bucket}`,
                                subtitle: `PRN ${p} · ${wtx}`,
                                amount_ugx: a.amount, ref: wtx,
                              });
                              setAllocs(prev => prev.map((x, idx) => idx === i ? { ...x, wtx } : x));
                              setFanCursor(i + 1);
                              await new Promise(r => setTimeout(r, 180));
                            }
                            setStage("done");
                            qc.invalidateQueries();
                          } else {
                            setStage("failed");
                            toast.error("Simulation failed — try again");
                          }
                        } catch (e: any) {
                          setStage("failed");
                          toast.error(e?.message ?? "Simulation failed");
                        }
                        setSimulating(false);
                      }}
                    >
                      {simulating ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Simulating…</> : "Simulate Payment (Demo)"}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="relative">
                    <Smartphone className="h-14 w-14" style={{ color: brandPrimary }} />
                    {stage === "push" && (
                      <motion.span
                        animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
                        className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--mustard)] text-[10px] font-bold text-foreground"
                      >!</motion.span>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-base" style={{ letterSpacing: '-0.01em' }}>
                      {stage === "push" && `Check your ${operator === "mtn" ? "MTN" : "Airtel"} phone`}
                      {stage === "confirm" && "Confirming with telco…"}
                      {stage === "fanout" && "Allocating to wallets…"}
                    </div>
                    <div className="mt-1 font-mono text-xs text-muted-foreground">{phone}</div>
                  </div>
                  <Card className="w-full bg-card p-3 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">PRN</span><span className="font-mono">{prn}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono">{fmtUGX(total)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Op TXN</span><span className="font-mono">{opTxn || "—"}</span></div>
                  </Card>
                  {stage !== "fanout" && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
                </>
              )}
            </div>

            {/* Live fan-out */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Wallet fan-out</div>
                <Badge variant="outline" className="text-[10px]">{fanCursor}/{allocs.length}</Badge>
              </div>
              <div className="max-h-[300px] overflow-auto rounded-lg border">
                {/* Desktop: table, Mobile: cards */}
                <table className="hidden w-full text-xs sm:table">
                  <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                    <tr><th className="px-2 py-1.5 text-left">Wallet</th><th className="px-2 py-1.5 text-left">Service</th><th className="px-2 py-1.5 text-right">Amount</th><th className="px-2 py-1.5 text-right">WTX</th></tr>
                  </thead>
                  <tbody>
                    <AnimatePresence initial={false}>
                      {allocs.map((a, i) => {
                        const b = BUCKETS.find(x => x.k === a.bucket)!;
                        return (
                          <motion.tr key={i} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }}
                            className={`border-t ${a.wtx ? "bg-emerald-50/60" : ""}`}>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className="grid h-5 w-5 place-items-center rounded-full text-[9px] text-white" style={{ background: a.tone }}>{a.initials}</span>
                                <span className="truncate">{a.name.split(" ")[0]}</span>
                              </div>
                            </td>
                            <td className="px-2 py-1.5">{b.emoji} {b.label}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{fmtUGX(a.amount)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-[10px]">
                              {a.wtx ? <span className="text-emerald-700">{a.wtx}</span> : <Loader2 className="ml-auto h-3 w-3 animate-spin text-muted-foreground" />}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
                {/* Mobile card list */}
                <div className="sm:hidden divide-y">
                  <AnimatePresence initial={false}>
                    {allocs.map((a, i) => {
                      const b = BUCKETS.find(x => x.k === a.bucket)!;
                      return (
                        <motion.div key={i} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }}
                          className={`p-3 ${a.wtx ? "bg-emerald-50/60" : ""}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="grid h-6 w-6 place-items-center rounded-full text-[10px] text-white" style={{ background: a.tone }}>{a.initials}</span>
                            <span className="text-xs font-medium truncate">{a.name.split(" ")[0]}</span>
                            <span className="ml-auto text-xs font-mono">{fmtUGX(a.amount)}</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                            <span>{b.emoji} {b.label}</span>
                            {a.wtx ? <span className="text-emerald-700 font-mono">{a.wtx}</span> : <Loader2 className="h-3 w-3 animate-spin" />}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="space-y-4 p-6 text-center">
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />
            </motion.div>
            <div>
              <div className="font-extrabold text-3xl" style={{ letterSpacing: '-0.02em' }}>{fmtUGX(total)} settled</div>
              <div className="mt-1 text-sm text-muted-foreground">
                {productName} · 1 push · {allocs.length} wallets credited · {fmtUGX(saved)} saved
              </div>
            </div>
            <div className="mx-auto max-w-md rounded-xl border bg-muted/30 p-3 text-left text-xs">
              <div className="flex items-center justify-between border-b pb-1.5">
                <span className="text-muted-foreground">Receipt</span>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => { navigator.clipboard.writeText(prn); toast.success("PRN copied"); }}>
                  <Copy className="mr-1 h-3 w-3" />{prn}
                </Button>
              </div>
              <div className="mt-2 space-y-1">
                {allocs.map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="truncate"><span className="mr-1">{BUCKETS.find(b => b.k === a.bucket)?.emoji}</span>{a.name} · {BUCKETS.find(b => b.k === a.bucket)?.label}</span>
                    <span className="font-mono">{fmtUGX(a.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => { reset(); setAllocs([]); }}><Zap className="mr-1 h-3 w-3" />New push</Button>
              <Button onClick={close} style={{ background: brandPrimary }} className="text-white hover:opacity-90">Done</Button>
            </div>
          </div>
        )}

        {stage === "failed" && (
          <div className="space-y-4 p-6 text-center">
            <XCircle className="mx-auto h-14 w-14 text-rose-600" />
            <div className="font-extrabold text-2xl" style={{ letterSpacing: '-0.02em' }}>Push declined</div>
            <p className="text-sm text-muted-foreground">The telco rejected the request. No funds moved.</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={close}>Close</Button>
              <Button onClick={() => { setStage("build"); }}>Retry</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, tone, bold }: { k: string; v: string; tone?: "rose" | "forest" | "mustard"; bold?: boolean }) {
  const c = tone === "rose" ? "text-rose-600" : tone === "forest" ? "text-emerald-700" : tone === "mustard" ? "text-amber-700" : "";
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{k}</span>
      <span className={`font-mono ${c}`}>{v}</span>
    </div>
  );
}
